// Sprint 12.5A (preview fix) — REAL generated-app preview tile.
//
// Shows the ACTUAL app UI: a sandboxed, scaled, non-interactive iframe of the
// deployed app (the same live-preview the editor uses), so a card reads as a real
// mini app preview — not a brand card. Honest fallback hierarchy, layered back→front:
//   1) branded Pomela placeholder (last resort, behind everything)
//   2) the app's OG card image (app-specific identity) — middle fallback
//   3) the live scaled app iframe (the real app UI) — primary, on top
// Whatever loads "highest" wins. No faked screenshots; the iframe is the real app.
//
// Existing infra used (no new pipeline): the app's deployed preview URL.
// pointer-events:none + sandbox keep it a safe, inert thumbnail.
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { PomelaMascot } from './PomelaMascot';

// Promote a same-origin /preview path to the cross-origin preview host when one is
// configured (mirrors PreviewPanel). Absolute URLs are used as-is.
const PREVIEW_ORIGIN = (import.meta as { env?: Record<string, string> }).env?.VITE_PREVIEW_ORIGIN || '';

function resolvePreviewSrc(appId: string, url?: string): string {
  if (url && /^https?:\/\//.test(url)) return url; // already absolute (the deployed preview)
  if (url && url.startsWith('/preview/')) return (PREVIEW_ORIGIN || '') + url;
  // §launch — a relay route is NOT reliably reachable from the creator origin (the Pages
  // relay 404s it), and a failed iframe load renders that 404 page OVER the OG card →
  // every thumbnail read "404 Not Found". So without a trustworthy absolute source we skip
  // the live iframe entirely and let the app's OG card (which loads reliably as an <img>)
  // be the thumbnail. `appId` retained for the signature; the caller decides framing from
  // the returned emptiness.
  return '';
}

/** Logical phone size the app renders at before scaling. A real phone viewport so
 *  the app paints its full hero/home; the card (overflow:hidden) crops the TOP slice. */
const LOGICAL_W = 390;
const LOGICAL_H = 760;

export function AppPreview({ appId, url, ogImageUrl }: { appId: string; url?: string; ogImageUrl?: string }): JSX.Element {
  const [ogFailed, setOgFailed] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const scalerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const src = resolvePreviewSrc(appId, url);
  const og = ogImageUrl && ogImageUrl.length > 0 ? ogImageUrl : `/v1/og/${encodeURIComponent(appId)}`;

  // Crisp scaling at any card width: render the iframe at LOGICAL_W and scale to fill.
  useLayoutEffect(() => {
    const scaler = scalerRef.current;
    if (!scaler) return;
    const apply = (): void => {
      const w = scaler.clientWidth; const h = scaler.clientHeight;
      if (!w || !h) return;
      const s = w / LOGICAL_W;
      const f = frameRef.current;
      if (f) {
        // render a full phone screen, scale to the card width; the card clips the top.
        f.style.width = `${LOGICAL_W}px`;
        f.style.height = `${Math.max(LOGICAL_H, Math.ceil(h / s))}px`;
        f.style.transform = `scale(${s})`;
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(scaler);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="pf-prev" aria-hidden="true">
      {/* 1 · branded placeholder (last resort, behind) */}
      <div className="pf-ph">
        <span className="pf-ph__phone"><PomelaMascot size={30} flat /></span>
      </div>
      {/* 2 · app-specific OG card (the reliable thumbnail; primary when there is no
             trustworthy live source) */}
      {!ogFailed && <img className="pf-prev__og" src={og} alt="" loading="lazy" onError={() => setOgFailed(true)} />}
      {/* 3 · the real app UI (primary) — only when we have a trustworthy absolute source
             (an unreachable relay route would render its 404 page over the OG card). */}
      {src ? (
        <div className="pf-prev__scaler" ref={scalerRef}>
          <iframe
            ref={frameRef}
            className={`pf-prev__frame${frameReady ? ' is-ready' : ''}`}
            src={src}
            title=""
            tabIndex={-1}
            scrolling="no"
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
            onLoad={() => setFrameReady(true)}
          />
        </div>
      ) : null}
    </div>
  );
}
